import { useTranslate } from '@refinedev/core';
import { Typography } from 'antd';
import { NovelistSignet } from '@/components/common/NovelistSignet';
import { GUTTER, MOBILE_SURFACE } from '@/components/mobile_preview/constants';
import { useMobilePreview } from '@/components/mobile_preview/mobilePreviewContext';
import { SwipeCarousel } from '@/components/mobile_preview/SwipeCarousel';
import { useLineupPublications } from '@/components/user_catalog/publications/useLineupPublications';
import { HomePreviewHeroSlide } from './HomePreviewHeroSlide';
import type { HomePreviewHero } from './homePreviewModel';

/** Over-cover copy stays white in both phone themes, as in the app. */
const ON_COVER_TEXT = '#FFFFFF';

interface HomePreviewHeroSectionProps {
  hero: HomePreviewHero;
}

/** The hero as the phone renders it — not an editor surface. The header
 * sits above the track, so covers slide beneath it. */
export const HomePreviewHeroSection = ({
  hero,
}: HomePreviewHeroSectionProps) => {
  const t = useTranslate();
  const { mobileTheme } = useMobilePreview();
  const { publicationById } = useLineupPublications(hero.publicationIds);

  const slides = hero.publicationIds.length
    ? hero.publicationIds.map((id, i) => (
        // Ids can repeat transiently while a row is being re-picked.
        <HomePreviewHeroSlide
          key={`${id}-${i}`}
          publication={publicationById.get(id)}
        />
      ))
    : [
        <HomePreviewHeroSlide
          key="empty"
          emptyNote={t('user_catalog.home_page.preview.empty_lineup')}
        />,
      ];

  return (
    <div style={{ position: 'relative' }}>
      <SwipeCarousel slides={slides} dotColor={ON_COVER_TEXT} />

      {/* The scaled frame lands the cover's bottom edge on a fractional
          device pixel, which antialiases into a hairline where the hero
          meets the feed. A surface strip straddling the seam hides it. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: -1,
          height: 2,
          background: MOBILE_SURFACE[mobileTheme],
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: `${GUTTER}px ${GUTTER}px 0`,
          color: ON_COVER_TEXT,
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        {/* The signet is a block-level svg, so it centers by layout, not
            by text alignment. */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <NovelistSignet height={18} />
        </div>
        <Typography.Title
          level={4}
          style={{
            color: 'inherit',
            margin: '14px 0 0',
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
          }}
        >
          {hero.name}
        </Typography.Title>
        {hero.description && (
          <div style={{ fontSize: 13, lineHeight: 1.4, marginTop: 6 }}>
            {hero.description}
          </div>
        )}
      </div>
    </div>
  );
};
